import React, { createContext, useCallback, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './Consent.css';
import { EASE } from './motion';

// Souhlas s načtením obsahu třetích stran. Jediná taková věc na webu je mapa Google;
// písma jsou přibalená k webu a analytika se nepoužívá.
// Uloženo v localStorage: { v, maps, date }. Zvýšením VERSION se lišta zobrazí všem znovu.
const STORAGE_KEY = 'kj-consent';
const VERSION = 1;

const readConsent = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved && saved.v === VERSION ? saved : null;
  } catch {
    return null;
  }
};

const ConsentContext = createContext(null);

export const useConsent = () => useContext(ConsentContext);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(readConsent);
  const [barOpen, setBarOpen] = useState(() => readConsent() === null);

  const save = useCallback((choice) => {
    const value = { v: VERSION, maps: Boolean(choice.maps), date: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // úložiště může být zablokované – volba pak platí jen do zavření stránky
    }
    setConsent(value);
    setBarOpen(false);
  }, []);

  const value = {
    maps: Boolean(consent?.maps),
    save,
    openSettings: () => setBarOpen(true),
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
      <AnimatePresence>{barOpen && <CookieBar initial={consent} onSave={save} />}</AnimatePresence>
    </ConsentContext.Provider>
  );
};

const CookieBar = ({ initial, onSave }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(Boolean(initial));
  const [maps, setMaps] = useState(Boolean(initial?.maps));

  return (
    <motion.aside
      className="consent"
      role="dialog"
      aria-labelledby="consent-title"
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.6, ease: EASE, delay: initial ? 0 : 0.8 }}
    >
      <h2 id="consent-title" className="consent__title">
        {t('consent.title')}
      </h2>
      <p className="consent__text">{t('consent.text')}</p>

      {detail && (
        <ul className="consent__cats">
          <li className="consent__cat">
            <div>
              <strong>{t('consent.catNecessary')}</strong>
              <p>{t('consent.catNecessaryDesc')}</p>
            </div>
            <span className="consent__always">{t('consent.always')}</span>
          </li>
          <li className="consent__cat">
            <div>
              <strong id="consent-maps">{t('consent.catMaps')}</strong>
              <p>{t('consent.catMapsDesc')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={maps}
              aria-labelledby="consent-maps"
              className={`consent__switch ${maps ? 'is-on' : ''}`}
              onClick={() => setMaps((m) => !m)}
            >
              <span />
            </button>
          </li>
        </ul>
      )}

      <div className="consent__actions">
        <button type="button" className="btn btn--accent btn--sm" onClick={() => onSave({ maps: true })}>
          {t('consent.acceptAll')}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onSave({ maps: false })}>
          {t('consent.necessary')}
        </button>
        {detail ? (
          <button type="button" className="consent__link" onClick={() => onSave({ maps })}>
            {t('consent.save')}
          </button>
        ) : (
          <button type="button" className="consent__link" onClick={() => setDetail(true)}>
            {t('consent.settings')}
          </button>
        )}
      </div>
    </motion.aside>
  );
};
